import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { usePrograms } from '../hooks/useAcademy';

const SelectedProgramContext = createContext(null);

// Sentinel value for "no program assigned" — distinct from null/undefined so
// it's a real, storable/comparable selection in the switcher and query
// filters (Unassigned groups/posts/events/players have programId == null on
// the doc itself; this is what the switcher stores to mean "show those").
export const UNASSIGNED_PROGRAM = '__unassigned__';

function pickDefaultProgramId(programs) {
  const today = new Date().toISOString().slice(0, 10);
  const started = programs
    .filter((p) => p.startDate && p.startDate <= today)
    .sort((a, b) => (b.startDate || '').localeCompare(a.startDate || ''));
  if (started[0]) return started[0].id;
  const upcoming = programs
    .filter((p) => p.startDate && p.startDate > today)
    .sort((a, b) => (a.startDate || '').localeCompare(b.startDate || ''));
  if (upcoming[0]) return upcoming[0].id;
  return UNASSIGNED_PROGRAM;
}

// Lets a coach/admin pick which program/season's Groups, Feed, Calendar and
// Messages they're viewing in the Academy Portal, so content from different
// seasons doesn't mix. Only meaningful once an academy has created 1+
// programs — with none, `selectedProgramId` stays null and every consumer
// should treat that as "no filtering, today's behavior", not "Unassigned".
// Guardians/players never pick one — their single player's own programId
// scopes their view silently (see useMyPlayer) — so this stays null for them
// even once the academy has programs.
export function SelectedProgramProvider({ children }) {
  const { academyId, canManageAcademyPortal } = useAuth();
  const { programs, loading } = usePrograms();
  const storageKey = academyId ? `academyPortal.programId.${academyId}` : null;
  const [selectedProgramId, setSelectedProgramId] = useState(null);

  useEffect(() => {
    if (!academyId || loading) return;
    if (programs.length === 0) { setSelectedProgramId(null); return; }
    let stored = null;
    try { stored = storageKey ? localStorage.getItem(storageKey) : null; } catch { /* private/blocked storage */ }
    if (stored && (stored === UNASSIGNED_PROGRAM || programs.some((p) => p.id === stored))) {
      setSelectedProgramId(stored);
    } else {
      setSelectedProgramId(pickDefaultProgramId(programs));
    }
  }, [academyId, loading, programs, storageKey]);

  const selectProgram = useCallback((programId) => {
    setSelectedProgramId(programId);
    try {
      if (!storageKey) return;
      if (programId) localStorage.setItem(storageKey, programId);
      else localStorage.removeItem(storageKey);
    } catch { /* private/blocked storage */ }
  }, [storageKey]);

  const value = useMemo(() => ({
    programs,
    programsLoading: loading,
    hasPrograms: programs.length > 0,
    selectedProgramId: canManageAcademyPortal ? selectedProgramId : null,
    selectProgram,
  }), [programs, loading, selectedProgramId, canManageAcademyPortal, selectProgram]);

  return <SelectedProgramContext.Provider value={value}>{children}</SelectedProgramContext.Provider>;
}

export function useSelectedProgram() {
  const ctx = useContext(SelectedProgramContext);
  if (!ctx) throw new Error('useSelectedProgram must be used within SelectedProgramProvider');
  return ctx;
}
